import { Controller } from '@hotwired/stimulus'
import { defineAsyncComponent } from 'vue'

const modules = import.meta.glob('../app/**/*.vue', { eager: false })

const componentMap = {}

for (const path in modules) {
    const name = path.split('/').pop().replace('.vue', '')
    const kebabName = name
        .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')
        .toLowerCase()
        .replace(/^-/, '')
    componentMap[kebabName] = defineAsyncComponent(modules[path])
}

// We now have a map of the available Vue components
//
// For example:
// const componentMap = {
//     'route-settings': () => import('/app/javascript/app/views/RouteSettings.vue'),
//     'company-select': () => import('/app/javascript/app/views/CompanySelect.vue'),
// }

export default class extends Controller {
    static targets = ['component']

    vueApps = []

    // For elements with data-vue-loader-target="component"
    // Inside this will contain HTML such as:
    //
    // <template data-vue-loader-target="component" data-vue-loader-skip-element-plus>
    //     <company-select></company-select>
    // </template>
    //
    // Vue natively is able to parse <company-select></company-select>
    // and knows to fetch CompanySelect.vue file via componentMap['company-select']()
    componentTargetConnected(element) {
        if (element.dataset.vueLoaderSkipElementPlus) {
            this.loadAndMountWithoutElementPlus(element)
        } else {
            this.loadAndMount(element)
        }
    }

    async loadAndMount(element) {
        // These dynamic imports can be called multiple times and ESM will handle it
        // without loading it twice
        const { createApp } = await import('vue')
        const { createPinia } = await import('pinia')
        const { dragscroll } = await import('vue-dragscroll')
        const ElementPlus = await import('element-plus')
        await import('../app/app.scss') // Mostly ElementPlus css

        const vueApp = createApp({
            components: componentMap,
            template: element.innerHTML,
        })
        vueApp.use(createPinia())
        vueApp.use(ElementPlus)
        vueApp.directive('dragscroll', dragscroll)

        this.#mountVueApp(element, vueApp)
    }

    // CompanySelect is on most pages but it doesn't need ElementPlus
    async loadAndMountWithoutElementPlus(element) {
        const { createApp } = await import('vue')

        const vueApp = createApp({
            components: componentMap,
            template: element.innerHTML,
        })

        this.#mountVueApp(element, vueApp)
    }

    disconnect() {
        this.vueApps.forEach(app => app.unmount())
        this.vueApps = []
    }

    // Normally vue will mount the app in the element itself. But because of
    // Turbo caching we need to mount it in a container div so it can safely be
    // re-mounted when the user presses back/forward in the browser
    #mountVueApp(element, vueApp) {
        // Look for existing container
        let mountDiv = element.nextElementSibling
        if (!mountDiv?.dataset?.vueLoaderContainer) {
            // Create new container
            mountDiv = document.createElement('div')
            mountDiv.dataset.vueLoaderContainer = ''
            element.insertAdjacentElement('afterend', mountDiv)
        }

        vueApp.mount(mountDiv)
        this.vueApps.push(vueApp)
    }
}
